'use client';

import Box from '@mui/material/Box';
import type {Theme} from '@mui/material/styles';
import {styled} from '@mui/material/styles';
import {Typography} from '../primitives';

export type EyebrowHeadingColor = string | ((theme: Theme) => string);

export interface EyebrowHeadingProps {
  eyebrow?: string;
  title: string;
  divider?: boolean;
  eyebrowColor?: EyebrowHeadingColor;
  titleColor?: EyebrowHeadingColor;
}

const Divider = styled('span')(({theme}) => ({
  display: 'block',
  width: 45,
  height: 2,
  borderRadius: theme.radii.full,
  backgroundColor: theme.palette.colors.surface.primary.default,
}));

const resolve = (theme: Theme, color?: EyebrowHeadingColor) => (typeof color === 'function' ? color(theme) : color);

export function EyebrowHeading({eyebrow, title, divider, eyebrowColor, titleColor}: EyebrowHeadingProps) {
  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'flex-start'}}>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start'}}>
        {eyebrow && (
          <Typography
            size="body-1"
            weight="regular"
            mobileSize="label"
            mobileWeight="regular"
            mobileLineHeight="20"
            as="p"
            sx={{
              letterSpacing: {xs: '2.6px', md: '0.15em'},
              color: theme => resolve(theme, eyebrowColor) ?? theme.palette.colors.text.primary.default,
              textTransform: 'uppercase',
            }}
          >
            {eyebrow}
          </Typography>
        )}
        <Typography
          size="title-1"
          weight="bold"
          mobileSize="title-5"
          mobileWeight="bold"
          as="h2"
          sx={{color: theme => resolve(theme, titleColor) ?? theme.palette.colors.text.neutral.strong, textTransform: 'capitalize'}}
        >
          {title}
        </Typography>
      </Box>
      {divider && <Divider />}
    </Box>
  );
}

EyebrowHeading.displayName = 'EyebrowHeading';
